if (!customElements.get('cn-bundle')) {
  customElements.define('cn-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.source = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.form?.querySelector('[type="submit"]');
      if (!this.source || !this.form || !this.button) return;

      this.buttonLabel = this.button.querySelector('span:not(.loading__spinner)');
      this.addLabel = this.buttonLabel?.textContent.trim() || this.button.textContent.trim();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };

      // Capture before Dawn's product-form handler. An incomplete bundle must
      // never fall back to a native single-item add, including from the sticky CTA.
      this.source.addEventListener('submit', (event) => {
        if (event.target !== this.form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addBundle();
      }, { capture: true, ...options });

      try {
        this.variants = JSON.parse(this.querySelector('[data-cn-bundle-variants]').textContent);
        this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency });
      } catch { return; }
      if (!Array.isArray(this.variants) || this.dataset.currency !== this.dataset.shopCurrency) return;

      this.cards = [...this.querySelectorAll('[data-tier]')];
      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-tier]');
        if (!card || event.target.matches('.cn-bundle__radio')) return;
        if (card !== this.activeCard) {
          // The first click activates an inactive offer. It does not also buy
          // the optional extra beneath that click or reset any saved selections.
          event.preventDefault();
          const radio = card.querySelector('.cn-bundle__radio');
          if (radio) radio.checked = true;
          this.sync();
          return;
        }
        const extra = event.target.closest('.cn-bundle__extra--paid');
        if (extra && !event.target.matches('[data-cn-addon]') && !event.target.closest('.cn-bundle__extra-title')) {
          // Match the source: checkbox and label text toggle, bare strip does not.
          event.preventDefault();
        }
      }, options);
      this.addEventListener('change', (event) => {
        if (event.target.matches('select')) this.normalizeRow(event.target);
        this.sync();
      }, options);

      this.source.classList.add('cn-bundle-active');
      this.hidden = false;
      this.sync();
    }

    format(cents) {
      return this.money.format(cents / 100);
    }

    getSelectedVariants(card) {
      return [...card.querySelectorAll('[data-cn-unit]')].map((row) => {
        const options = [...row.querySelectorAll('select')].map((select) => select.value);
        return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
      });
    }

    normalizeRow(changedSelect) {
      const row = changedSelect.closest('[data-cn-unit]');
      if (!row) return;
      const selects = [...row.querySelectorAll('select')];
      const changedIndex = selects.indexOf(changedSelect);
      const values = selects.map((select) => select.value);
      const exact = this.variants.find((variant) => variant.available && variant.options.every((value, index) => value === values[index]));
      const candidate = exact || this.variants.find((variant) => variant.available && variant.options.every((value, index) => index > changedIndex || value === values[index]));
      if (candidate) selects.forEach((select, index) => { if (index > changedIndex) select.value = candidate.options[index]; });
    }

    sync() {
      const selected = this.querySelector('.cn-bundle__radio:checked');
      if (!selected) return;
      this.quantity = Number(selected.value);
      this.activeCard = selected.closest('[data-tier]');

      this.cards.forEach((card) => {
        const active = card === this.activeCard;
        const quantity = Number(card.dataset.tier);
        const unitPrice = Number(card.dataset.unitPrice);
        const compareUnit = Number(card.dataset.compareUnit);
        const total = unitPrice * quantity;
        const original = compareUnit * quantity;
        const saved = Math.max(0, original - total);
        const variants = this.getSelectedVariants(card);

        card.classList.toggle('is-selected', active);
        const details = card.querySelector('.cn-bundle__details');
        if (details) details.hidden = !active;
        card.querySelector('.cn-bundle__radio')?.setAttribute('aria-expanded', String(active));
        card.querySelector('[data-cn-total]').textContent = this.format(total);
        const compare = card.querySelector('[data-cn-compare]');
        compare.textContent = this.format(original);
        compare.hidden = saved <= 0;
        const saving = card.querySelector('[data-cn-saving]');
        saving.textContent = this.dataset.labelSave.replace('[amount]', this.format(saved));
        saving.hidden = saved <= 0;

        [...card.querySelectorAll('[data-cn-unit]')].forEach((row, index) => {
          const variant = variants[index];
          const image = row.querySelector('img');
          if (image && variant?.image) {
            if (image.getAttribute('src') !== variant.image) {
              image.removeAttribute('srcset');
              image.src = variant.image;
            }
            image.alt = variant.title;
          }
          const selects = [...row.querySelectorAll('select')];
          const values = selects.map((select) => select.value);
          selects.forEach((select, optionIndex) => {
            [...select.options].forEach((option) => {
              option.disabled = !this.variants.some((item) => item.available && item.options.every((value, index) => index > optionIndex || value === (index === optionIndex ? option.value : values[index])));
            });
          });
        });

        if (active) {
          this.selectedVariants = variants;
          this.total = total;
          this.originalTotal = original;
        }
      });

      this.addons = [...this.activeCard.querySelectorAll('[data-cn-addon]:checked')].map((input) => ({
        key: input.dataset.cnAddon,
        quantity: 1,
        price: Number(input.dataset.price)
      }));
      this.gifts = [...this.activeCard.querySelectorAll('[data-cn-gift]')].map((element) => ({
        key: element.dataset.cnGift,
        quantity: 1
      }));
      this.addonsTotal = this.addons.reduce((sum, addon) => sum + addon.price, 0);
      this.checkoutTotal = this.total + this.addonsTotal;
      const valid = Number.isSafeInteger(this.total) && this.total >= 0 && this.selectedVariants.length === this.quantity && this.selectedVariants.every((variant) => variant?.available);
      this.button.disabled = !valid;
      if (this.buttonLabel) this.buttonLabel.textContent = valid ? this.addLabel : this.dataset.labelUnavailable;
      this.source.classList.toggle('cn-bundle-multiple', this.quantity > 1);

      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput && this.selectedVariants[0] && idInput.value !== String(this.selectedVariants[0].id)) {
        idInput.value = String(this.selectedVariants[0].id);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const quantityInput = this.source.querySelector('.quantity__input');
      if (quantityInput) quantityInput.value = '1';
      this.summary = this.dataset.labelSelection.replace('[quantity]', String(this.quantity));
      this.querySelector('[data-cn-summary]').textContent = this.summary + ' — ' + this.format(this.checkoutTotal);
      this.source.dispatchEvent(new CustomEvent('cn:bundle-change', { bubbles: true }));
    }

    getSelection() {
      if (!this.activeCard) return null;
      return { quantity: this.quantity, total: this.format(this.total), label: this.summary };
    }

    // Read-only handoff for a future verified app adapter. Gift keys must be
    // mapped to real merchandise and server discounts before submitting it.
    getCheckoutSelection() {
      if (!this.activeCard) return null;
      return {
        quantity: this.quantity,
        currency: this.dataset.currency,
        variants: this.selectedVariants.map((variant) => ({ id: variant?.id, quantity: 1 })),
        addons: this.addons.map((addon) => ({ ...addon })),
        gifts: this.gifts.map((gift) => ({ ...gift })),
        hoodieTotal: this.total,
        addonsTotal: this.addonsTotal,
        expectedTotal: this.checkoutTotal
      };
    }

    addBundle() {
      // Checkout remains staged for every setting, including "connected".
      // Release this guard only after the installed app's hoodie tiers, gift
      // merchandise, quantity repricing and direct checkout have been verified.
      // There is intentionally no native-form, cart API or redirect fallback.
      const message = this.dataset.labelStaged || this.dataset.labelError;
      const owner = this.form.closest('product-form');
      owner?.handleErrorMessage?.(message);
      const nativeError = owner?.querySelector('.product-form__error-message-wrapper');
      let feedback = nativeError;
      if (!nativeError || nativeError.hidden) {
        if (!this.stagedMessage) {
          this.stagedMessage = document.createElement('p');
          this.stagedMessage.className = 'product-form__error-message';
          this.stagedMessage.setAttribute('role', 'alert');
          this.append(this.stagedMessage);
        }
        this.stagedMessage.textContent = message;
        feedback = this.stagedMessage;
      }
      const rect = feedback.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) feedback.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.source?.classList.remove('cn-bundle-active', 'cn-bundle-multiple');
    }
  });
}
